/* Styles - Statics */
import '../../styles/SearchOrders/searchOrders.scss';
import LoadingGif from '../../static/loader.gif';
/* Libs */
import React, { useEffect, useReducer, useCallback, useState, useRef } from 'react';
/* Components */
import { CustomerOrders } from './customerOrders/CustomerOrders';
/* Controllers - Helpers */
import { signIn } from '../../controllers/basicAuth';
import { getOrdersByEmail } from '../../helpers/getOrders';

// Constants
const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/i;

// Action types constants
const ACTIONS = {
  SET_LOADING: 'SET_LOADING',
  SET_SHOW_ORDERS: 'SET_SHOW_ORDERS',
  SET_ERROR_INPUT: 'SET_ERROR_INPUT',
  SET_ERROR_EMAIL: 'SET_ERROR_EMAIL',
  SET_DATA: 'SET_DATA',
  SET_TOKEN: 'SET_TOKEN',
  SET_INPUT_EMAIL: 'SET_INPUT_EMAIL',
};

// Initial state
const initialState = {
  isLoading: false,
  showOrders: false,
  hasInputError: false,
  hasEmailError: false,
  orders: [],
  token: '',
  email: '',
};

// Reducer function
function reducer(state, action) {
  switch (action.type) {
    case ACTIONS.SET_LOADING:
      return { ...state, isLoading: action.payload };
    case ACTIONS.SET_SHOW_ORDERS:
      return { ...state, showOrders: action.payload };
    case ACTIONS.SET_ERROR_INPUT:
      return { ...state, hasInputError: action.payload };
    case ACTIONS.SET_ERROR_EMAIL:
      return { ...state, hasEmailError: action.payload };
    case ACTIONS.SET_DATA:
      return { ...state, orders: action.payload };
    case ACTIONS.SET_TOKEN:
      return { ...state, token: action.payload };
    case ACTIONS.SET_INPUT_EMAIL:
      return { ...state, email: action.payload };
    default:
      return state;
  }
}

/**
 * SearchOrders Component
 * Handles searching for customer orders by email address
 */
export const SearchOrders = () => {
  // State management
  const [state, dispatch] = useReducer(reducer, initialState);
  const [announcementMessage, setAnnouncementMessage] = useState('');
  const { isLoading, showOrders, hasInputError, hasEmailError, orders, token, email } = state;
  const emailInputRef = useRef(null);

  // Fetch authentication token on component mount
  useEffect(() => {
    const getAuthToken = async () => {
      try {
        const authToken = await signIn();
        dispatch({ type: ACTIONS.SET_TOKEN, payload: authToken });
      } catch (error) {
        console.error('Authentication error:', error);
        setAnnouncementMessage('Authentication failed. Please try again later.');
      }
    };

    getAuthToken();
  }, []);

  /**
   * Validates email format and emptiness
   * @param {string} emailValue - Email to validate
   * @returns {boolean} - True if email is valid
   */
  const validateEmail = useCallback(emailValue => {
    const isEmpty = !emailValue?.trim();
    const isInvalid = !EMAIL_REGEX.test(emailValue);

    dispatch({ type: ACTIONS.SET_ERROR_INPUT, payload: isEmpty });
    dispatch({ type: ACTIONS.SET_ERROR_EMAIL, payload: !isEmpty && isInvalid });

    return !isEmpty && !isInvalid;
  }, []);

  /**
   * Updates email state and validates on input change
   */
  const handleInputChange = ({ target }) => {
    const { value } = target;
    dispatch({ type: ACTIONS.SET_INPUT_EMAIL, payload: value });
    validateEmail(value);
  };

  /**
   * Fetches customer orders by email
   */
  const fetchOrders = async () => {
    if (!token) {
      console.error('No authentication token available');
      setAnnouncementMessage('Authentication failed. Please try again later.');
      return;
    }

    try {
      // Update UI state for loading
      dispatch({ type: ACTIONS.SET_LOADING, payload: true });
      dispatch({ type: ACTIONS.SET_SHOW_ORDERS, payload: false });
      setAnnouncementMessage('Searching for orders...');

      // Fetch data
      const fetchedOrders = await getOrdersByEmail(email, token);
      dispatch({ type: ACTIONS.SET_DATA, payload: fetchedOrders });
      dispatch({ type: ACTIONS.SET_SHOW_ORDERS, payload: true });

      // Update announcement message based on results
      const orderCount = fetchedOrders?.length || 0;
      setAnnouncementMessage(
        orderCount > 0
          ? `Found ${orderCount} order${orderCount === 1 ? '' : 's'}.`
          : 'No orders found for this email address.'
      );
    } catch (error) {
      console.error('Error fetching orders:', error);
      setAnnouncementMessage('Error fetching orders. Please try again later.');
    } finally {
      dispatch({ type: ACTIONS.SET_LOADING, payload: false });
    }
  };

  /**
   * Form submission handler
   */
  const handleSubmit = e => {
    e.preventDefault();
    if (validateEmail(email)) {
      fetchOrders();
    }
  };

  // Dynamic button text based on state
  const buttonText = orders.length > 0 ? 'Search Again for Orders' : 'FIND MY ORDERS';

  // Component UI sections
  const renderFormSection = () => (
    <form
      onSubmit={handleSubmit}
      className="font-family container-680"
      aria-labelledby="form-title">
      <div className="container">
        <div role="heading" aria-level="2" id="form-title" className="form-title sr-only">
          Order Search Form
        </div>

        <div className="py-4">
          <label className="label resizeLabels" htmlFor="emailOrders">
            <span className="label-text">
              <b>EMAIL ADDRESS</b>
            </span>
          </label>
          <input
            type="email"
            id="emailOrders"
            name="emailOrders"
            value={email}
            onChange={handleInputChange}
            className={`form-input field__input emailInput ${
              hasInputError || hasEmailError ? 'error-border' : ''
            }`}
            aria-invalid={hasInputError || hasEmailError}
            aria-describedby={
              hasInputError ? 'input-error' : hasEmailError ? 'email-format-error' : undefined
            }
            required
            autoComplete="email"
            placeholder="Enter your email address"
            ref={emailInputRef}
            aria-required="true"
          />
          {hasInputError && (
            <p id="input-error" className="form-text error" role="alert">
              Email is required. Please enter your email address.
            </p>
          )}
          {hasEmailError && (
            <p id="email-format-error" className="form-text error" role="alert">
              Please enter a valid email address (e.g., name@example.com).
            </p>
          )}
        </div>

        <div className="container-button py-4">
          <button
            type="submit"
            className="we-search-button ff-button--primary"
            disabled={hasEmailError || isLoading}
            aria-busy={isLoading}
            aria-disabled={hasEmailError || isLoading}>
            {buttonText}
            <span className="sr-only">
              {isLoading
                ? 'Searching...'
                : orders.length > 0
                  ? 'Search again for orders'
                  : 'Find my orders'}
            </span>
          </button>
          {isLoading && (
            <div className="sr-only" aria-live="assertive">
              Processing your request
            </div>
          )}
        </div>
      </div>
    </form>
  );

  const renderLoadingSection = () =>
    isLoading && (
      <div className="centered centered-items we-loading" role="status" aria-live="assertive">
        <p>Loading orders...</p>
        <img src={LoadingGif} alt="" aria-hidden="true" />
      </div>
    );

  const renderResultsSection = () => (
    <div aria-live="polite" className="py-4">
      {showOrders && orders.length > 0 && (
        <>
          <h2 id="results-title" className="sr-only">
            Order Results
          </h2>
          <CustomerOrders
            email={email}
            data={orders}
            token={token}
            aria-labelledby="results-title"
          />
        </>
      )}

      {showOrders && orders.length === 0 && (
        <p className="no-orders-message" role="status">
          No orders found for this email address. Please check the email address and try again.
        </p>
      )}
    </div>
  );

  return (
    <div className="search-orders" role="main">
      <h1 id="page-title" className="sr-only">
        Search Orders
      </h1>
      <div
        className="sr-only"
        role="status"
        aria-live="polite"
        aria-atomic="true"
        id="status-announcer">
        {announcementMessage}
      </div>
      <div className="skip-link-container">
        <a href="#emailOrders" className="skip-link" tabIndex="0">
          Skip to search form
        </a>
      </div>

      {renderFormSection()}
      {renderLoadingSection()}
      {renderResultsSection()}
    </div>
  );
};

export default SearchOrders;
